import type { Filters } from '@/types/Filters'
import type { PeriodRange } from '@/types/Common'

const ALL = 'ALL' as const

const DEFAULT_DATE_RANGE: Readonly<PeriodRange> = {
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
}

const DEFAULT_FILTERS: Readonly<Filters> = {
  dateRange: DEFAULT_DATE_RANGE,
  agentNames: [],
  languages: [],
  statuses: [],
  listConfiguratorColumns: [],
}

function normalizeFilters(raw?: unknown): Filters {
  let parsed: Partial<Filters> = {}

  if (raw && typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw) as Partial<Filters>
    } catch (e) {
      console.warn('Invalid filters JSON:', e)
    }
  } else if (raw && typeof raw === 'object') {
    parsed = raw
  }

  return {
    ...DEFAULT_FILTERS,
    ...parsed,
    dateRange: {
      ...DEFAULT_DATE_RANGE,
      ...(parsed.dateRange ?? {}),
    },
    agentNames: parsed.agentNames ?? [],
    languages: parsed.languages ?? [],
    statuses: parsed.statuses ?? [],
    listConfiguratorColumns: parsed.listConfiguratorColumns ?? [],
    listUsersColumns: parsed.listUsersColumns ?? [],
  }
}

export const useFilters = (input: string | Filters, onChange?: (filters: Filters) => void) => {
  const localFilters = ref<Filters>(normalizeFilters(input))

  const fieldComputed = <K extends keyof Filters>(key: K) =>
    computed<Filters[K]>({
      get: () => localFilters.value[key],
      set: (val) => {
        localFilters.value[key] = val
      },
    })

  const period = fieldComputed('dateRange')
  const selectedAgentNames = fieldComputed('agentNames')
  const selectedLanguages = fieldComputed('languages')
  const selectedStatuses = fieldComputed('statuses')
  const selectedConfiguratorColumns = fieldComputed('listConfiguratorColumns')
  const selectedUserColumns = fieldComputed('listUsersColumns')

  const selectedAgentsWithAll = computed<string[]>({
    get: () => localFilters.value.agentNames ?? [],
    set: (val: string[]) => {
      localFilters.value.agentNames = val
      onChange?.(localFilters.value)
    },
  })

  const setFilters = (f: string | Filters) => {
    localFilters.value = normalizeFilters(f)
  }

  const resetFilters = () => {
    localFilters.value = normalizeFilters(undefined)
  }

  const applyPeriod = (range?: Partial<PeriodRange>) => {
    if (range) {
      period.value = { ...DEFAULT_DATE_RANGE, ...range }
    } else {
      localFilters.value.dateRange = { ...DEFAULT_DATE_RANGE, ...period.value }
    }
    onChange?.(localFilters.value)
  }

  return {
    localFilters,
    period,
    selectedAgentNames,
    selectedLanguages,
    selectedStatuses,
    selectedAgentsWithAll,
    selectedConfiguratorColumns,
    selectedUserColumns,
    ALL,
    setFilters,
    resetFilters,
    applyPeriod,
  }
}
